"use client";

// Referral-partner request form for the #partners section of the Body Ballancer
// hub. Requests a complimentary session — it never books one; Nicola follows up
// and books it herself (see /api/body-ballancer/partner-interest).

import React, { useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import TrackedLink from "@components/TrackedLink";
import { track } from "@lib/track";
import { PARTNER_TYPES } from "./partnerTypes";

const COMPONENT = "BallancerPartnerForm";
const LABEL = "/treatments/body-ballancer";

type Fields = { name: string; business: string; email: string; phone: string };
const EMPTY: Fields = { name: "", business: "", email: "", phone: "" };

const FIELDS: { key: keyof Fields; label: string; type: string; autoComplete: string }[] = [
  { key: "name", label: "Your name", type: "text", autoComplete: "name" },
  { key: "business", label: "Business name", type: "text", autoComplete: "organization" },
  { key: "email", label: "Email", type: "email", autoComplete: "email" },
  { key: "phone", label: "Phone", type: "tel", autoComplete: "tel" },
];

export default function BallancerPartnerForm() {
  const [form, setForm] = useState<Fields>(EMPTY);
  const [errors, setErrors] = useState<Partial<Fields>>({});
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [serverError, setServerError] = useState("");
  const [hp, setHp] = useState("");
  // Unticked by default. Consent must be an action, never a default.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  // Optional. "" = not answered; never block a request on it.
  const [businessType, setBusinessType] = useState("");
  const mountedAt = useRef(Date.now()); // form-fill time, for the server's timing check

  const validate = () => {
    const errs: Partial<Fields> = {};
    if (!form.name.trim()) errs.name = "Please enter your name.";
    if (!form.business.trim()) errs.business = "Please enter your business name.";
    // Test the TRIMMED value — autocomplete often appends a space, and the
    // server trims. Testing the raw value refused good addresses on two forms.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Please enter a valid email address.";
    if (!form.phone.trim()) errs.phone = "Please enter a phone number.";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "loading") return;
    const errs = validate();

    // Attempt, before the validation gate — same convention as contact_submit.
    try {
      track(
        "ballancer_partner_submit",
        { label: LABEL, component: COMPONENT },
        { event: "ballancer_partner_submit", label: LABEL, component: COMPONENT, meta: { valid: Object.keys(errs).length === 0, marketingOptIn } }
      );
    } catch {}

    setErrors(errs);
    if (Object.keys(errs).length) return;

    setStatus("loading");
    setServerError("");
    try {
      const res = await fetch("/api/body-ballancer/partner-interest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          business: form.business.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          businessType: businessType || undefined,
          marketingOptIn,
          hp,
          elapsedMs: Date.now() - mountedAt.current,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed to send");

      // Success — only after the API accepts it.
      try {
        track(
          "ballancer_partner_sent",
          { label: LABEL, component: COMPONENT },
          { event: "ballancer_partner_sent", label: LABEL, component: COMPONENT, meta: { businessType: businessType || null } }
        );
      } catch {}
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setServerError(err instanceof Error && err.message ? err.message : "Something went wrong. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div role="status" className="rounded-2xl border border-[var(--color-brand)]/20 bg-white p-8 text-center">
        <p className="text-xl font-light text-gray-800 mb-2">Thank you — we&apos;ll be in touch.</p>
        <p className="text-gray-500">
          Nicola will contact you within a week to arrange your complimentary session. A copy of your details is on its way to your inbox.
        </p>
        {/* Same destination and event as the section's own read-more link;
            meta.position tells the two apart. */}
        <TrackedLink
          href="#top"
          variant="text"
          size="md"
          color="base"
          event="ballancer_partner_read_more"
          label="Read More"
          component={COMPONENT}
          meta={{ position: "partners-success" }}
          className="mt-4 inline-flex items-center gap-1 !px-0"
        >
          In the meantime, read more about the treatment
          <ChevronRight className="h-4 w-4 -rotate-90" />
        </TrackedLink>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="relative rounded-2xl border border-gray-200 bg-white p-6 sm:p-8">
      {/* Honeypot — off-screen rather than display:none, same field name as the
          newsletter and contact forms. The server decides; the client just sends it. */}
      <input
        type="text"
        name="contact_website"
        tabIndex={-1}
        autoComplete="off"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        className="absolute -left-[9999px] h-0 w-0 opacity-0"
        aria-hidden="true"
      />
      <div className="grid gap-5 sm:grid-cols-2">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label htmlFor={`partner-${f.key}`} className="block text-sm font-medium text-gray-700 mb-1">
              {f.label} <span aria-hidden="true">*</span>
            </label>
            <input
              id={`partner-${f.key}`}
              type={f.type}
              autoComplete={f.autoComplete}
              value={form[f.key]}
              onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
              aria-invalid={!!errors[f.key]}
              aria-describedby={errors[f.key] ? `partner-${f.key}-error` : undefined}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-800 focus:border-[var(--color-brand)] focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
            />
            {errors[f.key] && (
              <p id={`partner-${f.key}-error`} className="mt-1 text-sm text-red-600">{errors[f.key]}</p>
            )}
          </div>
        ))}
        <div className="sm:col-span-2">
          <label htmlFor="partner-businessType" className="block text-sm font-medium text-gray-700 mb-1">
            Type of business <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <select
            id="partner-businessType"
            value={businessType}
            onChange={(e) => setBusinessType(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-gray-800 focus:border-[var(--color-brand)] focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          >
            <option value="">Select…</option>
            {PARTNER_TYPES.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>
      <label className="mt-6 flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
        <input
          type="checkbox"
          checked={marketingOptIn}
          onChange={(e) => setMarketingOptIn(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-brand)]"
        />
        <span>
          Keep me updated with news, offers and events from CF Organic Wellness. Unsubscribe any time.
        </span>
      </label>
      {status === "error" && (
        <p role="alert" className="mt-5 text-sm text-red-600">{serverError}</p>
      )}
      <button
        type="submit"
        disabled={status === "loading"}
        className="mt-6 inline-flex items-center justify-center rounded-full bg-[var(--color-brand)] px-6 py-3 font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
      >
        {status === "loading" ? "Sending…" : "Request my complimentary session"}
      </button>
      <p className="mt-4 text-xs text-gray-400 leading-relaxed">
        We use these details to arrange your session and talk about referrals. You&apos;ll only receive marketing emails if you tick the box above.
      </p>
    </form>
  );
}
